import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { OctagonX } from 'lucide-react'
import { clearKill } from '@/api/priceRollout'
import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { BestaetigungsDialog } from '@/components/rollout/BestaetigungsDialog'
import { liveZaehlung } from '@/lib/rollout-klartext'
import { cn } from '@/lib/utils'
import { useRolloutStatus, useRolloutStatusNeuLaden } from './useRolloutStatus'

function Schalter({ an, children }: { an: boolean; children: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]', an ? 'bg-panel text-ink' : 'bg-panel text-muted')}>
      <span aria-hidden className={cn('size-1.5 rounded-full', an ? 'bg-success' : 'bg-line')} />
      {children} {an ? 'an' : 'aus'}
    </span>
  )
}

function clearKillFehler(e: unknown): string {
  if (e instanceof ApiError && e.status === 403) return `Keine Berechtigung – Not-Aus aufheben braucht das Recht „pricing:rollout“. (${e.message})`
  return e instanceof ApiError ? e.message : 'Not-Aus konnte nicht aufgehoben werden'
}

/** Seitenkopf: Not-Aus, LIVE-Freigabe (Zahl jetzt live wirkender Rollouts), Tagesprüfung, Start-Schritt. */
export function BetriebBanner() {
  const { data } = useRolloutStatus()
  const neuLaden = useRolloutStatusNeuLaden()
  const [offen, setOffen] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const aufheben = useMutation({
    mutationFn: clearKill,
    onSuccess: () => { setOffen(false); setFehler(null); toast.success('Not-Aus aufgehoben') },
    onError: (e) => setFehler(clearKillFehler(e)),
    onSettled: () => { void neuLaden() },
  })
  const b = data?.betrieb
  if (!b) return null
  const live = liveZaehlung(data.rollouts)
  return (
    <div className="space-y-2">
      {b.kill_aktiv && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md border border-coral bg-coral-soft px-4 py-2.5">
          <OctagonX className="size-4 text-coral" aria-hidden />
          <span className="flex-1 text-sm font-semibold text-ink">Not-Aus aktiv – kein Rollout schreibt Preise, auch kein Start und kein Fortsetzen.</span>
          <Button size="sm" variant="outline" onClick={() => { setFehler(null); setOffen(true) }}>Not-Aus aufheben</Button>
        </div>
      )}
      <div
        data-testid="betrieb-banner"
        className={cn(
          'flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border-l-4 bg-panel-2 px-4 py-2.5',
          b.live_writes ? 'border-coral' : 'border-line',
        )}
      >
        <span className={cn('text-sm', b.live_writes ? 'font-semibold text-ink' : 'text-ink-2')}>
          {b.live_writes
            ? `LIVE-Freigabe an – ${live} Rollout${live === 1 ? ' schreibt' : 's schreiben'} echte Preise`
            : 'LIVE-Freigabe aus – alle Rollouts schreiben nur ins Protokoll'}
        </span>
        <span className="flex flex-wrap gap-1.5 sm:ml-auto">
          <Schalter an={b.tagespruefung}>Tagesprüfung</Schalter>
          <Schalter an={b.start_schritt}>Start-Schritt</Schalter>
          {b.tagespruefung && (
            <span className="rounded-full bg-panel px-2 py-0.5 text-[11px] text-muted">
              {b.heute_gelaufen ? 'heute schon gelaufen' : 'heute noch nicht gelaufen'}
            </span>
          )}
        </span>
      </div>
      <BestaetigungsDialog
        offen={offen}
        titel="Not-Aus aufheben"
        beschreibung="Danach dürfen Tageslauf, Start und Fortsetzen wieder Preise schreiben. Pausierte Rollouts bleiben pausiert."
        aktion="Not-Aus aufheben"
        gefahr
        busy={aufheben.isPending}
        fehler={fehler}
        onAbbrechen={() => { setOffen(false); setFehler(null) }}
        onBestaetigen={() => aufheben.mutate()}
      />
    </div>
  )
}
